import { create } from "zustand";
import { getApiUrl } from "@/utils/apiConfig";

export type EventStatus = "draft" | "pending" | "approved" | "rejected" | "annule";

export interface PendingEvent {
  id: string;
  user_id?: string;
  partyAcronym: string;
  title: string;
  description: string;
  date: string;
  location: string;
  type: string;
  contactName: string;
  contactEmail: string;
  submittedAt: string;
  status: EventStatus;
  imageUrl?: string;
  rejectionReason?: string;
  organizerStatus?: string;
  registrationType?: "public" | "mandatory";
  registrationLink?: string;
  externalLink?: string;
  publishStatus?: "draft" | "published";
  createdAt?: string;
  updatedAt?: string;
}

interface EventsState {
  events: PendingEvent[];
  isLoading: boolean;
  error: string | null;
  fetchEvents: (isPublic?: boolean) => Promise<void>;
  saveEvent: (event: Partial<PendingEvent>) => Promise<PendingEvent | null>;
  updateEventStatus: (id: string, status: EventStatus, rejectionReason?: string) => Promise<boolean>;
  deleteEvent: (id: string) => Promise<boolean>;
}

export const useEventsStore = create<EventsState>((set, get) => ({
  events: [],
  isLoading: false,
  error: null,

  fetchEvents: async (isPublic = false) => {
    set({ isLoading: true, error: null });
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem('kaynin_auth_token') : null;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const url = `${getApiUrl()}/events.php${isPublic ? '?public=1' : ''}`;

      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error("Failed to fetch events");

      const data = await res.json();

      const formatted: PendingEvent[] = data.map((e: any) => ({
        ...e,
        submittedAt: e.createdAt ? e.createdAt.split(' ')[0] : new Date().toISOString().split('T')[0]
      }));

      set({ events: formatted, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  saveEvent: async (event) => {
    set({ isLoading: true, error: null });
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem('kaynin_auth_token') : null;
      if (!token) throw new Error("Non authentifié");

      const method = event.id ? 'PUT' : 'POST';
      const res = await fetch(`${getApiUrl()}/events.php`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(event)
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to save event");
      }

      // Reload to get the new id/status properly
      await get().fetchEvents();
      return get().events.find(e => e.title === event.title) || null;
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
      return null;
    }
  },

  updateEventStatus: async (id, status, rejectionReason) => {
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem('kaynin_auth_token') : null;
      if (!token) throw new Error("Non authentifié");

      const payload: any = { id, status };
      if (rejectionReason) payload.rejectionReason = rejectionReason;

      const res = await fetch(`${getApiUrl()}/events.php`, {
        method: 'PUT',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Failed to update status");

      // Update local state without full reload
      set(state => ({
        events: state.events.map(e => e.id === id ? { ...e, status, rejectionReason } : e)
      }));
      return true;
    } catch (err: any) {
      set({ error: err.message });
      return false;
    }
  },

  deleteEvent: async (id) => {
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem('kaynin_auth_token') : null;
      if (!token) throw new Error("Non authentifié");

      const res = await fetch(`${getApiUrl()}/events.php?id=${id}`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error("Failed to delete event");

      set(state => ({
        events: state.events.filter(e => e.id !== id)
      }));
      return true;
    } catch (err: any) {
      set({ error: err.message });
      return false;
    }
  }
}));

// Export a direct fetch function for components that don't need the store initially
export async function fetchPublicEventsFromServer(): Promise<PendingEvent[]> {
  try {
    const res = await fetch(`${getApiUrl()}/events.php?public=1`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((e: any) => ({
      ...e,
      submittedAt: e.createdAt ? e.createdAt.split(' ')[0] : new Date().toISOString().split('T')[0]
    }));
  } catch (err) {
    return [];
  }
}
